import React from 'react';
import PropTypes from 'prop-types';
import {
  withStyles,
  Button,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from '@material-ui/core';

// components
import TagList from '../../../widgets/TagList';
import EventDataList from '../../../widgets/EventDataList';
import Property from '../../../widgets/Property';
import User from '../../../widgets/User';
import EventUpload from '../../../widgets/EventUpload';
import AttachFileModal from '../../../modals/AttachFileModal';
import EventStatus from '../../../widgets/EventStatus';
import DueDate from '../../../widgets/DueDate';

// edit event widgets
import EditEventName from '../../../widgets/EditEventName';
import EditEventDescription from '../../../widgets/EditEventDescription';
import EditEventTags from '../../../widgets/EditEventTags';
import EditEventData from '../../../widgets/EditEventData';
import EditEventDueDate from '../../../widgets/EditEventDueDate';

// utils
import {
  getColorForItem,
  getIconForItem,
} from '../../../../utils/iconHelper';
import { getCommentStringFromMentionString } from '../../../../utils/mentions-helper';
import { formatDateString } from '../../../../utils/dateHelper';

// local event page helper components
import EventActionsMenu from '../EventActionsMenu';
import EventHistoryCard from '../EventHistoryCard';

import styles from './styles';

export class EventDataCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editMode: props.openInEditMode,
      editHasChanged: false,
      editName: {
        value: null,
        isValid: true,
      },
      editDescription: {
        value: null,
        isValid: true,
      },
      editTags: { value: null },
      editEventData: {
        value: null,
        isValid: true,
      },
      editDueDate: { value: null },
      openAttachFileModal: false,
    };
  }

  handleEditClick = () => {
    this.setState({ editMode: true });
  }

  handleCancelEditing = () => {
    this.setState({
      editMode: false,
      editHasChanged: false,
      editName: {
        value: null,
        isValid: true,
      },
      editTags: { value: null },
      editEventData: {
        value: null,
        isValid: true,
      },
      editDueDate: { value: null },
    });
  }

  handleSaveEdit = () => {
    const request = {
      name: this.state.editName.value,
      description: this.state.editDescription.value,
      tags: this.state.editTags.value === null
        ? null
        : this.state.editTags.value.map((tag) => tag.id),
      eventData: this.state.editEventData.value,
      dueDate: this.state.editDueDate.value,
    };

    this.props.patchEvent(this.props.selectedEvent.id, request);
    this.handleCancelEditing();
  }

  handleNameUpdate = (updatedName, isValid) => {
    this.setState({
      editName: {
        value: updatedName,
        isValid,
      },
      editHasChanged: true,
    });
  }

  handleDescriptionUpdate = (updatedDescription, isValid) => {
    this.setState({
      editDescription: {
        value: updatedDescription,
        isValid,
      },
      editHasChanged: true,
    });
  }

  handleTagsUpdate = (updatedTags) => {
    this.setState({
      editTags: { value: updatedTags },
      editHasChanged: true,
    });
  }

  handleAttachFileClick = () => {
    this.setState({
      openAttachFileModal: true,
      editMode: false,
    });
  }

  handleCloseAttachFileModal = () => {
    this.setState({ openAttachFileModal: false });
  }

  handleEventDataUpdate = (updatedEventData) => {
    let isValid = true;
    Object.keys(updatedEventData).forEach((key) => {
      if (updatedEventData[key].error) {
        isValid = false;
      }
    });

    this.setState({
      editEventData: {
        value: updatedEventData,
        isValid,
      },
      editHasChanged: true,
    });
  }

  handleDueDateChange = (updatedDueDate) => {
    this.setState({
      editDueDate: { value: updatedDueDate },
      editHasChanged: true,
    });
  }

  render() {
    const {
      selectedEvent,
      classes,
      statuses,
      tags: propTags,
      tagTypes,
      eventData: propEventData,
      tenantUsers,
      changeStatusClick,
      isAdmin,
      isRestricted,
      canAttachFiles,
      eventData: allEventData,
    } = this.props;

    const {
      editMode,
      editName: { isValid: nameIsValid },
      editEventData: { isValid: eventDataIsValid },
      editDueDate: { value: editDueDateValue },
      editHasChanged,
      openAttachFileModal,
    } = this.state;

    const {
      currentEventStatus = {
        status: {
          id: -1,
          name: 'No status',
        },
        createdDate: null,
        assignment: null,
      },
      tags = [],
      eventData = {},
      eventStatus = [],
      dueDate,
      archived,
      completeDate,
    } = selectedEvent;

    const statusColor
      = getColorForItem(statuses, currentEventStatus.status.id);
    const statusIcon
      = getIconForItem(statuses, currentEventStatus.status.id);

    return (
      <div>
        <CardHeader
          classes={{ root: classes.cardHeader }}
          title={(
            (editMode && selectedEvent.name)
              ? (
                <EditEventName
                  name={selectedEvent.name}
                  onUpdate={this.handleNameUpdate}
                />
              )
              : (
                <h3 className={classes.eventName}>
                  {selectedEvent.name}
                </h3>
              )
          )}
          subheader={
            editMode
              ? ''
              : (
                <div>
                  {
                    selectedEvent.createdDate
                    && <div>{formatDateString(selectedEvent.createdDate)} - #{selectedEvent.id}</div>
                  }
                </div>
              ) // eslint-disable-line react/jsx-indent
          }
          action={
            !isRestricted
            && (
              <EventActionsMenu
                attachFileClick={this.handleAttachFileClick}
                historyClick={(e) => changeStatusClick(e, 1)}
                editClick={this.handleEditClick}
              />
            ) // eslint-disable-line react/jsx-indent
          }
        />
        <CardContent>
          <Grid container>
            <Grid item xs={12} md={3} lg={2}>
              <Typography variant="subtitle2">Details</Typography>
            </Grid>
            <Grid item xs={12} md={9} lg={10} container className={classes.section}>
              <Grid item xs={12}>
                <Property label="Status">
                  <EventStatus
                    eventStatus={currentEventStatus}
                    icon={statusIcon}
                    color={statusColor}
                    hideComment
                    archived={archived}
                    completed={!!completeDate}
                  />
                </Property>
              </Grid>
              <Grid item xs={12} md={10} lg={8} xl={6}>
                {
                  (editMode && selectedEvent.description !== null)
                    ? (
                      <EditEventDescription
                        description={selectedEvent.description}
                        onUpdate={this.handleDescriptionUpdate}
                      />
                    )
                    : (
                      <Property label="Description">
                        {
                          selectedEvent.description
                          || getCommentStringFromMentionString((eventStatus[0] || {}).statusComment)
                        }
                      </Property>
                    )
                }
              </Grid>
              <Grid item xs={12}>
                <Property label="Assignment">
                  {currentEventStatus.assignment
                    ? (currentEventStatus.assignment.split(/(?=@\[)/)
                      .map((user) => (
                        <User
                          key={user.auth0UserId}
                          chip
                          user={{ auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1] }}
                        />
                      )))
                    : (
                      '-'
                    )}
                </Property>
              </Grid>
              <Grid item xs={12}>
                <Property label="Due Date">
                  {
                    !editMode
                      ? <DueDate dueDate={dueDate} />
                      : (
                        <EditEventDueDate
                          dueDate={(editDueDateValue || selectedEvent.dueDate)
                            ? new Date(editDueDateValue || selectedEvent.dueDate)
                            : null}
                          onUpdate={this.handleDueDateChange}
                        />
                      )
                  }
                </Property>
              </Grid>
              <Grid item xs={12}>
                <Property label="Created by">
                  <User
                    chip
                    user={{
                      email: selectedEvent.createdByEmail,
                      userFullName: selectedEvent.createdBy,
                    }}
                  />
                </Property>
              </Grid>

              <Grid item xs={12}>
                <Property label="Tags">
                  {
                    !editMode
                      ? <TagList tags={tags} />
                      : (
                        <EditEventTags
                          selectedTags={tags}
                          tags={propTags}
                          tagTypes={tagTypes}
                          onUpdate={this.handleTagsUpdate}
                          isAdmin={isAdmin}
                        />
                      )
                  }
                </Property>
              </Grid>

              <Grid item xs={12}>
                <Property label="Created">
                  {
                    selectedEvent.createdDate
                    && formatDateString(selectedEvent.createdDate)
                  }
                </Property>
              </Grid>

              <Grid item xs={12}>
                <Property label="Last Updated">
                  {
                    selectedEvent.createdDate
                    && formatDateString(selectedEvent.updatedDate)
                  }
                </Property>
              </Grid>

              {
                selectedEvent.completeDate
                && (
                  <Grid item xs={12}>
                    <Property label="Date completed">
                      {
                        selectedEvent.completeDate
                        && formatDateString(selectedEvent.completeDate)
                      }
                    </Property>
                  </Grid>
                )
              }

              {
                canAttachFiles
                  && selectedEvent.eventUploads
                  && selectedEvent.eventUploads.length > 0
                  && (
                    <Property label="Attachments">
                      {
                        selectedEvent.eventUploads.map((upload) => (
                          <div
                            className={classes.eventUploadContainer}
                            key={upload.id}
                          >
                            <EventUpload eventUpload={upload} editable={editMode} />
                          </div>
                        ))
                      }
                    </Property>
                  )
              }
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12} md={3} lg={2}>
              <Typography variant="subtitle2">Custom Fields</Typography>
            </Grid>
            <Grid item xs={12} md={9} lg={10} container className={classes.section}>
              {
                !editMode
                  ? (
                    <EventDataList
                      eventData={eventData}
                      allEventData={allEventData}
                    />
                  )
                  : (
                    <EditEventData
                      selectedEventData={eventData}
                      eventData={propEventData}
                      onUpdate={this.handleEventDataUpdate}
                    />
                  )
              }
            </Grid>
          </Grid>
          <Grid container>
            {
              editMode
                && (
                  <Grid item xs={12}>
                    <div className={classes.buttonContainer}>
                      <Button onClick={this.handleCancelEditing}>
                        Cancel
                      </Button>
                      <Button
                        onClick={this.handleSaveEdit}
                        disabled={!nameIsValid || !eventDataIsValid || !editHasChanged}
                        color="primary"
                        variant="contained"
                      >
                        Save
                      </Button>
                    </div>
                  </Grid>
                )
            }
          </Grid>

          <Grid container className={classes.section}>
            {/* <Grid item xs={12} md={4}>
              <Typography variant="subtitle2">Notifications</Typography>
              <EventNotificationsCard
                eventNotifications={eventNotifications}
                isFetchingEventNotifications={isFetchingEventNotifications}
              />
            </Grid> */}

            <Grid item xs={12} md={3} lg={2}>
              <Typography variant="subtitle2">Activity</Typography>
            </Grid>
            <Grid item xs={12} md={9} lg={10} container className={classes.section}>
              <EventHistoryCard isRestricted={isRestricted} tenantUsers={tenantUsers} />
            </Grid>
          </Grid>
        </CardContent>

        <AttachFileModal
          isOpen={openAttachFileModal}
          handleCloseModal={this.handleCloseAttachFileModal}
          eventId={selectedEvent.id}
        />
      </div>
    );
  }
}

EventDataCardComponent.propTypes = {
  openInEditMode: PropTypes.bool,
  selectedEvent: PropTypes.object,
  classes: PropTypes.object,
  statuses: PropTypes.array,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  eventData: PropTypes.array,
  tenantUsers: PropTypes.array,
  patchEvent: PropTypes.func,
  changeStatusClick: PropTypes.func,
  isAdmin: PropTypes.bool,
  isRestricted: PropTypes.bool,
  canAttachFiles: PropTypes.bool,
};

EventDataCardComponent.defaultProps = {
  openInEditMode: false,
  selectedEvent: {},
  classes: {},
  statuses: [],
  tags: [],
  tagTypes: [],
  eventData: [],
  tenantUsers: [],
  patchEvent: () => { },
  changeStatusClick: () => { },
  isAdmin: false,
  isRestricted: false,
  canAttachFiles: false,
};

export default withStyles(styles)(EventDataCardComponent);
